import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import { get, keyBy, merge } from 'lodash'
import { Link as GatsbyLink } from 'gatsby'
import { Box, FormControlLabel, Link, Switch, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import GreenArrow from '../../../stories/assets/icons/up_arrow.svg'
import {
  changeIndicator,
  formatDate,
  formatDollars,
  formatDollarsWithSymbol,
  formatLinkWithTicker,
  formatPercentage,
} from '../../../utils/functions'
import { dateRenderer, percentageRenderer } from '../../Tables/ScrollingTable'
import WhiteScrollingTable from '../../Tables/WhiteScrollingTable'
import Popover from '../../Popover/Popover'
import Html from '../../Html/Html'
import AsOfDate from '../../AsOfDate'
import TodaysDistribution from '../TodaysDistribution'
import { useSearchFilters } from '../../../providers/search'

const MARGIN_TOP_FIRST = { marginTop: '30px' }
const MARGIN_TOP_OTHER = { marginTop: '40px' }

const DummyCellClassname = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.alternative,
}))

const DUMMY_CELL_ELEMENT = <DummyCellClassname />

const WHITE_SCROLLING_TABLE_PROPS = {
  adjustFirstColumnWidth: true,
  maxFirstColumnWidth: '25%',
}

const TableTitle = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.text.primary,
  backgroundColor: '#F3F3F3',
  display: 'table-cell',
  margin: 0,
  width: '100%',
  padding: '0 !important',
}))

const Cell = styled(Box)(({ theme }) => ({
  ...theme.typography.body2,
  display: 'block',
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.fontSize,
    lineHeight: '20px',
  },
}))

const NumberContainer = styled(Box)(() => ({
  display: 'block',
  textAlign: 'right',
  '@media (max-width: 720px)': {
    textAlign: 'left',
  },
}))

const NumberCell = styled(Box)(() => ({
  position: 'relative',
  display: 'inline-block',
  fontSize: '16px',
}))

const ArrowedNumberCell = styled(NumberCell)(({ change }) => {
  const isPositive = changeIndicator(change) === 'fundChangeUp'
  const isNegative = changeIndicator(change) === 'fundChangeDown'
  return {
    position: 'relative',
    paddingLeft: 0,
    color: isPositive ? '#1A8D60' : isNegative ? '#d31e1e' : 'inherit',
    '&::before': {
      content: isPositive || isNegative ? '""' : undefined,
      position: 'absolute',
      top: 0,
      bottom: 0,
      margin: 'auto',
      width: '14px',
      height: '18px',
      left: '-25px',
      background: `url(${GreenArrow}) no-repeat 0 0`,
      backgroundSize: '100%',
      transform: isNegative ? 'rotate(180deg)' : 'none',
      filter: isPositive
        ? 'invert(37%) sepia(80%) saturate(442%) hue-rotate(105deg) brightness(99%) contrast(86%)'
        : isNegative
          ? 'invert(51%) sepia(79%) saturate(7456%) hue-rotate(345deg) brightness(81%) contrast(108%)'
          : 'none',
    },
  }
})

const FundName = styled(Typography)(({ theme }) => ({
  fontSize: '14px !important',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '22px',
  display: 'inline',
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: '20px',
  },
}))

const FundNameSubtitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightLight,
  fontSize: '12px !important',
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.fontSize * 0.75,
    lineHeight: '18px',
  },
}))

const FundNameAsOfDate = styled(Typography)(() => ({
  lineHeight: '18px',
  fontWeight: 400,
  '@media (max-width: 720px)': {
    fontSize: '10px',
  },
}))

const FundNameContainerStyled = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  whiteSpace: 'normal',
  textAlign: 'left',
  width: '100%',
  padding: '0',
}))

const FundNameTooltipFlex = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minWidth: '100%',
  gap: 10,
}))

const DateLogoRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  marginTop: 24,
  '& img': {
    height: '25px',
    width: 'auto',
  },
}))

const MorningStarValuesRoot = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: 4,
}))

const MorningStarValues = styled('span')(() => ({
  lineHeight: 1,
  fontSize: 12,
  whiteSpace: 'nowrap',
}))

const Subtitle = styled(Typography)(() => ({
  display: 'inline-block',
  fontSize: 20,
  fontWeight: 500,
  letterSpacing: -0.4,
  marginBottom: 16,
}))

const ControlsContainerStyled = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
}))

const TextLink = styled('button')(() => ({
  background: 'none',
  color: '#D8B45A',
  textDecoration: 'underline',
  border: 'none',
  padding: 0,
  font: 'inherit',
  cursor: 'pointer',
  outline: 'inherit',
}))

const TableContentStyled = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    padding: '0 16px',
    marginLeft: -16,
    marginRight: -16,
  },
}))

function getHtml(html, text) {
  return html ? <Html content={html} /> : text || ''
}

const findFundByShareClass = (fund, activeShareClassId) => {
  if (!fund.fund_classes || !Array.isArray(fund.fund_classes)) {
    return fund && Object.keys(fund).length > 0 ? fund : null
  }
  return fund.fund_classes.find((shareClass) => {
    return shareClass.share_class[0].id === activeShareClassId
  })
}

const TYPOGRAPHY_STYLES = { fontWeight: 500 }

const fundTickerRenderer = ({ row, value }) => {
  if (row && row.benchmarkIndex) {
    return <span className="benchmark-name">{value}</span>
  }
  return (
    <span>
      <Link
        component={GatsbyLink}
        to={row.url || formatLinkWithTicker(row)}
        underline="none"
      >
        <Typography sx={TYPOGRAPHY_STYLES} color="textPrimary" variant="body2">
          {value}
        </Typography>
      </Link>
    </span>
  )
}

const withCellStyle = (WrappedComponent, StyleComponent) => {
  const StyledComponent = (props) => (
    <NumberContainer>
      <StyleComponent>
        <WrappedComponent {...props} />
      </StyleComponent>
    </NumberContainer>
  )
  StyledComponent.displayName = `withCellStyle(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`
  return StyledComponent
}

const AllFundPrices = ({
  html_content_before,
  data,
  activeShareClass,
  fundGrouping,
  showYieldColumns = true,
  showValuationColumns: initialShowValuationColumns = true,
}) => {
  const [showValuationColumns, setShowValuationColumns] = useState(
    initialShowValuationColumns
  )
  const [showMarketValues, setShowMarketValues] = useState(false)

  const { filters, applyFilter } = useSearchFilters()

  const handleMarketValuesChange = useCallback(() => {
    setShowMarketValues((prev) => !prev)
  }, [])

  useEffect(() => {
    if (Array.isArray(data) && data.length > 0) {
      const hasValuation = !!data.find((item) => item?.valuation)
      setShowValuationColumns(hasValuation)
    } else if (typeof data === 'object' && data !== null) {
      const queryData = Array.isArray(data.data) ? data.data : []
      const hasValuation = !!queryData.find((item) => item?.valuation)
      setShowValuationColumns(hasValuation)
    } else {
      setShowValuationColumns(false)
    }
  }, [data])

  const asOfDate = useMemo(() => {
    const queryData = Array.isArray(data) ? data : get(data, 'data', [])
    const found = queryData.find((d) => get(d, 'valuation.calendar.date'))
    return found ? get(found, 'valuation.calendar.date') : null
  }, [data])

  const sectionedTables = useMemo(() => {
    if (!fundGrouping?.group) return []

    const queryData = Array.isArray(data) ? data : get(data, 'data', [])
    const filteredQueryData = queryData.filter((fund) => fund !== null)
    const fundData = keyBy(filteredQueryData, 'ticker')

    const tables = fundGrouping.group.reduce((tablesAcc, fundGroup) => {
      const tableData = fundGroup.products.reduce((tableAcc, fund) => {
        const fundByShareClass = findFundByShareClass(
          fund,
          activeShareClass?.id
        )
        if (fundByShareClass) {
          const { index_ticker, ticker = index_ticker } = fundByShareClass
          const fundObj = merge(
            fundData[ticker],
            { ...fundByShareClass, ticker },
            fund
          )
          if (fundObj) {
            tableAcc.push(fundObj)
          }
        }
        return tableAcc
      }, [])
      tablesAcc.push({
        title: fundGroup.title,
        data: tableData,
        showYieldColumns: fundGroup.show_yield_columns,
      })
      return tablesAcc
    }, [])

    const filteredTables = []
    tables.forEach((table) => {
      const response = applyFilter(table, filters)
      if (response) {
        filteredTables.push(response)
      }
    })

    const dataTables = []
    filteredTables.forEach((table) => {
      if (table.data.length > 0) {
        dataTables.push({
          innerTitle: <TableTitle component="h3">{table.title}</TableTitle>,
          dummyCellClassname: DUMMY_CELL_ELEMENT,
          title: table.title,
          isYields: table.showYieldColumns,
        })
        const newTable = table.data.map((row) =>
          Object.assign(row, { category: table.title.split(' ')[0] })
        )
        dataTables.push(...newTable)
      }
    })

    const sectionedTablesFinal = []
    let tempTable = []
    let setHeader = false
    dataTables.forEach((entry, idx) => {
      if (entry.innerTitle && !setHeader) {
        setHeader = true
      } else if (entry.innerTitle && setHeader) {
        sectionedTablesFinal.push(tempTable)
        tempTable = []
      }
      tempTable.push(entry)
      if (idx === dataTables.length - 1) {
        sectionedTablesFinal.push(tempTable)
      }
    })

    return sectionedTablesFinal
  }, [data, activeShareClass, fundGrouping, applyFilter, filters])

  const PriceChangeRenderer = useMemo(() => {
    const Renderer = ({ value }) => (
      <NumberContainer>
        <ArrowedNumberCell change={value}>
          {typeof value === 'number' ? formatDollarsWithSymbol(value) : '--'}
        </ArrowedNumberCell>
      </NumberContainer>
    )
    Renderer.displayName = 'PriceChangeRenderer'
    return Renderer
  }, [])

  const PercentageChangeRenderer = useMemo(() => {
    const Renderer = ({ value }) => (
      <NumberContainer>
        <ArrowedNumberCell change={value}>
          {typeof value === 'number' ? formatPercentage(value) : '--'}
        </ArrowedNumberCell>
      </NumberContainer>
    )
    Renderer.displayName = 'PercentageChangeRenderer'
    return Renderer
  }, [])

  const fundNameRenderer = (props) => {
    if (!props.row) return null

    const {
      expense_ratio,
      shortDescription,
      averageAnnualReturnSinceInception,
    } = props.row
    const sinceInception = get(
      averageAnnualReturnSinceInception,
      'fundReturn.sinceInception'
    )
    const sinceInceptionAsOfDate = sinceInception
      ? get(averageAnnualReturnSinceInception, 'calendar.date')
      : null

    const renderExpenseRatio = (label, ratio, popups) => {
      if (!ratio) return null
      return (
        <p>
          <b>{label}: </b>
          {ratio}%
          {popups && (
            <>
              <br />
              {popups.map((popup, i) => (
                <Fragment key={i}>
                  {getHtml(popup.html, popup.text)}
                  <br />
                </Fragment>
              ))}
            </>
          )}
        </p>
      )
    }

    const htmlContent =
      expense_ratio?.net || expense_ratio?.gross || expense_ratio?.adjusted ? (
        <>
          {shortDescription && <p>{shortDescription}</p>}
          {renderExpenseRatio(
            'Net Expense Ratio',
            expense_ratio?.net,
            expense_ratio?.net_popup
          )}
          {renderExpenseRatio(
            'Gross Expense Ratio',
            expense_ratio?.gross,
            expense_ratio?.gross_popup
          )}
          {renderExpenseRatio(
            'Adjusted Expense Ratio',
            expense_ratio?.adjusted,
            expense_ratio?.adjusted_popup
          )}
          {sinceInception && (
            <p>
              <b>Average Annual Total Return Since inception: </b>
              {formatPercentage(sinceInception)}
              <br />
              As of {formatDate(sinceInceptionAsOfDate)}
            </p>
          )}
        </>
      ) : null

    const morningstar_rating = props.row?.fund_classes?.find((fund_class) => {
      return fund_class?.share_class?.[0]?.title === activeShareClass?.title
    })?.morningstar_rating

    return (
      <FundNameContainerStyled>
        <FundNameTooltipFlex>
          <Link
            component={GatsbyLink}
            to={props.row.url || `${formatLinkWithTicker(props.row)}`}
            underline="none"
          >
            <FundName variant="body2" color="textPrimary">
              {props.value}
            </FundName>
          </Link>
          {htmlContent && (
            <Popover header={props.value} htmlContent={htmlContent} />
          )}
        </FundNameTooltipFlex>
        <FundNameSubtitle>
          {get(props, 'row.product_subadviser[0].subadviser_firm[0].title', '')}
        </FundNameSubtitle>
        {morningstar_rating?.length ? (
          <DateLogoRow>
            {morningstar_rating[0]?.rating_image?.localAsset && (
              <GatsbyImage
                image={getImage(morningstar_rating[0].rating_image.localAsset)}
                alt={
                  morningstar_rating[0].rating_image.description ||
                  'Rating Image'
                }
              />
            )}
            <MorningStarValuesRoot>
              {morningstar_rating[0]?.analyst_driven_pct && (
                <MorningStarValues>
                  {`Analyst-Driven % ${morningstar_rating[0].analyst_driven_pct}`}
                </MorningStarValues>
              )}
              {morningstar_rating[0]?.data_coverage_pct && (
                <MorningStarValues>
                  {`Data Coverage % ${morningstar_rating[0].data_coverage_pct}`}
                </MorningStarValues>
              )}
            </MorningStarValuesRoot>
            <FundNameAsOfDate variant="subtitle2" component="h2">
              As of {formatDate(morningstar_rating[0]?.rating_date)}
            </FundNameAsOfDate>
          </DateLogoRow>
        ) : null}
      </FundNameContainerStyled>
    )
  }

  const priceRenderer = (props) => {
    if (!props.row) return null
    const todaysDistribution = get(props, 'row.valuation.todaysDistribution')
    return (
      <NumberContainer>
        <NumberCell>
          {typeof props.value === 'number' ? formatDollars(props.value) : '--'}
          {todaysDistribution && (
            <TodaysDistribution todaysDistribution={todaysDistribution} />
          )}
        </NumberCell>
      </NumberContainer>
    )
  }

  const accessors = useMemo(() => {
    const base = {
      price: (d) => get(d, 'valuation.nav'),
      valueChange: (d) => get(d, 'valuation.navChangeDollars'),
      percentChange: (d) => get(d, 'valuation.navChangePercent'),
      ytdReturn: (d) => get(d, 'valuation.ytdReturn'),
    }
    if (showMarketValues) {
      base.price = (d) => get(d, 'valuation.etfMarketPrice.marketPrice')
      base.valueChange = (d) => get(d, 'valuation.etfMarketPrice.changeDollars')
      base.percentChange = (d) =>
        get(d, 'valuation.etfMarketPrice.changePercent')
      base.ytdReturn = (d) => get(d, 'valuation.etfMarketPrice.ytdReturn')
    }
    return base
  }, [showMarketValues])

  const yieldColumnsArr = showYieldColumns
    ? [
        {
          id: 'subsidizedYield',
          Header: 'Subsidized Yield (%)',
          accessor: (d) => get(d, 'yields.current30Day'),
          Cell: withCellStyle(percentageRenderer, Cell),
          sortable: true,
        },
        {
          id: 'unsubsidizedYield',
          Header: 'Unsubsidized Yield (%)',
          accessor: (d) => get(d, 'yields.current30DayUnsubsidized'),
          Cell: withCellStyle(percentageRenderer, Cell),
          sortable: true,
        },
      ]
    : []

  // If valuation columns are shown, define them
  const valuationColumns = showValuationColumns
    ? [
        {
          id: 'price',
          Header: 'Price',
          accessor: accessors.price,
          Cell: priceRenderer,
          sortable: true,
        },
        {
          id: 'valueChange',
          Header: 'Change ($)',
          accessor: accessors.valueChange,
          Cell: PriceChangeRenderer,
          sortable: true,
        },
        {
          id: 'percentChange',
          Header: 'Change (%)',
          accessor: accessors.percentChange,
          Cell: PercentageChangeRenderer,
          sortable: true,
        },
        {
          id: 'ytdReturn',
          Header: 'YTD Return',
          accessor: accessors.ytdReturn,
          Cell: withCellStyle(percentageRenderer, Cell),
          sortable: true,
        },
      ]
    : []

  // Final columns
  const columns = [
    {
      id: 'title',
      Header: showValuationColumns ? 'Fund Name' : 'Index Name',
      accessor: 'title',
      Cell: fundNameRenderer,
      sortable: true,
    },
    {
      id: 'ticker',
      Header: showValuationColumns ? 'Ticker' : 'Index Symbol',
      accessor: 'ticker',
      Cell: fundTickerRenderer,
      sortable: true,
    },
    {
      id: 'inception_date',
      Header: 'Inception Date',
      accessor: 'inception_date',
      Cell: withCellStyle(dateRenderer, Cell),
      sortable: true,
    },
    ...valuationColumns,
    ...yieldColumnsArr,
  ]

  // For cases when yield columns should NOT be displayed
  const noYieldColumns = columns.filter(
    (col) =>
      col.Header !== 'Subsidized Yield (%)' &&
      col.Header !== 'Unsubsidized Yield (%)'
  )

  // Do we have Market Price data?
  const hasMarketPrice = useMemo(() => {
    const queryData = Array.isArray(data) ? data : get(data, 'data', [])
    return queryData.some((tickerData) => {
      const marketPrice = get(
        tickerData,
        'valuation.etfMarketPrice.marketPrice'
      )
      return typeof marketPrice === 'number' && !isNaN(marketPrice)
    })
  }, [data])

  // Stable reload function
  const handleReload = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }, [])

  const switchControl = useCallback(
    <Switch
      checked={showMarketValues}
      onChange={handleMarketValuesChange}
      name="custom-checked"
    />,
    [showMarketValues]
  )

  return (
    <Box>
      {html_content_before &&
        html_content_before.map((content_block, idx) =>
          content_block ? (
            <Html key={idx} content={content_block.html} className="htmlLink" />
          ) : null
        )}

      {!fundGrouping?.title?.toLowerCase().includes('etf') && !!data && (
        <Subtitle>Prices & Yields</Subtitle>
      )}

      <ControlsContainerStyled>
        <AsOfDate date={asOfDate} />
        {hasMarketPrice && (
          <FormControlLabel
            control={switchControl}
            label="NAV/Market Price"
            labelPlacement="start"
          />
        )}
      </ControlsContainerStyled>

      {sectionedTables.length === 0 ? (
        <Typography variant="body2">
          Your criteria produced no results.{' '}
          <TextLink onClick={handleReload}>Clear filter</TextLink> to explore
          our products.
        </Typography>
      ) : (
        sectionedTables.map((table, index) => (
          <TableContentStyled
            key={index}
            // Avoid inline style objects:
            sx={index === 0 ? MARGIN_TOP_FIRST : MARGIN_TOP_OTHER}
          >
            <WhiteScrollingTable
              columns={table[0].isYields ? columns : noYieldColumns}
              data={table}
              {...WHITE_SCROLLING_TABLE_PROPS}
            />
          </TableContentStyled>
        ))
      )}
    </Box>
  )
}

AllFundPrices.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  fundGrouping: PropTypes.shape({
    group: PropTypes.array,
    title: PropTypes.string,
  }),
  activeShareClass: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  html_content_before: PropTypes.array,
  showYieldColumns: PropTypes.bool,
  showValuationColumns: PropTypes.bool,
}

AllFundPrices.displayName = 'AllFundPrices'

export default AllFundPrices